import { ClassPhaseProp } from '../../../../store/companies/companiesTypes';
import React, { useEffect, useState } from 'react';
import { CoursePhaseContainer, PhaseRow, PhaseRowContainer } from './style';
import { ITags } from '../../../../store/providers/providerTypes';
import messages from './Messages';
import { MultiDatePicker } from '../../../../components/MyDatePicker/MultiDatePicker';
import { convertDigitToProperFormat } from '../../../../utils/number';
import Collapsible from '../../../../components/Collapsible/Collapsible';
import { black, blue, red } from '../../../../utils/style/colors';

interface ICoursePhase {
  classPhaseProps: ClassPhaseProp[];
  groupId: string;
  tags: ITags['data'];
  phasePropOverride?: Array<{
    phaseTagId: number;
    phaseStageId: number;
    startDate?: number;
    endDate?: number;
  }>;
  defaultOpen?: boolean;
  onPhaseChange?: (
    overrides: Array<{
      phaseTagId: number;
      phaseStageId: number;
      startDate?: number;
      endDate?: number;
    }>
  ) => void;
}

interface IPhaseTimeSpan {
  [stage: number]: {
    [phase: number]: {
      phaseId: number;
      startDate: number;
      endDate: number;
      isStartOverride: boolean;
      isEndOverride: boolean;
    };
  };
}

interface IWithoutStage {
  [phase: number]: Array<{
    startDate: number;
    endDate: number;
    phaseId: number;
    isStartOverride: boolean;
    isEndOverride: boolean;
  }>;
}

const ONE_DAY = 24 * 3600 * 1000;

const CoursePhaseList: React.FC<ICoursePhase> = ({
  classPhaseProps,
  groupId,
  tags,
  phasePropOverride,
  onPhaseChange,
  defaultOpen,
}) => {
  const [phaseTime, setPhaseTime]: [
    IPhaseTimeSpan,
    (
      value: ((prevState: IPhaseTimeSpan) => IPhaseTimeSpan) | IPhaseTimeSpan
    ) => void,
  ] = useState<IPhaseTimeSpan>({});
  const [withoutPhase, setWithouPhase] = useState<IWithoutStage>({});

  useEffect(() => {
    setPhaseTime({});
    setWithouPhase({});
    if (classPhaseProps.length) {
      classPhaseProps
        .filter((phase: ClassPhaseProp) => !phase.disabled)
        .map((phase: ClassPhaseProp) => {
          const override = phasePropOverride?.find(
            (o) =>
              o.phaseTagId === phase.phaseTagId &&
              o.phaseStageId === phase.phaseStageId
          );

          if (phase.limitStateToOne) {
            setWithouPhase((prevWithoutPhase) => ({
              ...prevWithoutPhase,
              [phase.phaseTagId]: [
                ...(prevWithoutPhase[phase.phaseTagId] || []),
                {
                  phaseId: phase.phaseTagId,
                  startDate: override?.startDate
                    ? override?.startDate
                    : phase.phaseStartDates[groupId],
                  endDate: override?.endDate
                    ? override?.endDate
                    : phase.phaseEndDates[groupId],
                  isStartOverride: !!override?.startDate,
                  isEndOverride: !!override?.endDate,
                },
              ],
            }));
          } else {
            setPhaseTime((prevPhaseTime) => ({
              ...prevPhaseTime,
              [phase.phaseStageId]: {
                ...prevPhaseTime[phase.phaseStageId],
                [phase.phaseTagId]: {
                  phaseId: phase.phaseTagId,
                  startDate: override?.startDate
                    ? override?.startDate
                    : phase.phaseStartDates[groupId],
                  endDate: override?.endDate
                    ? override?.endDate
                    : phase.phaseEndDates[groupId],
                  isStartOverride: !!override?.startDate,
                  isEndOverride: !!override?.endDate,
                },
              },
            }));
          }
        });
    }
  }, [groupId, phasePropOverride, classPhaseProps]);

  const handleChangeCoursePhaseTime = (
    stageId: number,
    phaseId: number,
    changeDate: { startDate?: number; endDate?: number }
  ) => {
    setPhaseTime((prev) => {
      const next: IPhaseTimeSpan = { ...prev };

      if (!next[stageId] || !next[stageId][phaseId]) {
        return prev;
      }

      next[stageId] = {
        ...next[stageId],
        [phaseId]: {
          ...next[stageId][phaseId],
          ...changeDate,
        },
      };

      const overrides = Object.entries(next)
        .flatMap(([stage, phases]) =>
          Object.entries(phases).map(([phase, data]) => ({
            phaseTagId: Number(phase),
            phaseStageId: Number(stage),
            startDate: (data as any).startDate,
            endDate: (data as any).endDate,
          }))
        )
        .filter((override) => {
          const originalPhase = classPhaseProps.find(
            (p) =>
              p.phaseTagId === override.phaseTagId &&
              p.phaseStageId === override.phaseStageId
          );
          return (
            originalPhase &&
            (override.startDate !== originalPhase.phaseStartDates[groupId] ||
              override.endDate !== originalPhase.phaseEndDates[groupId])
          );
        });
      onPhaseChange?.(overrides);

      return next;
    });
  };

  const handleChangeWithoutPhaseTime = (
    tagId: number,
    index: number,
    changeDate: { startDate?: number; endDate?: number }
  ) => {
    setWithouPhase((prev) => {
      const next: IWithoutStage = { ...prev };
      const arr = next[tagId] ? [...next[tagId]] : [];

      if (!arr.length || index < 0 || index >= arr.length) return prev;

      arr[index] = { ...arr[index], ...changeDate };
      next[tagId] = arr;

      const overrides = Object.entries(next)
        .flatMap(([phaseTagId, phases]) =>
          phases.map(
            (phase: {
              startDate: number;
              endDate: number;
              phaseId: number;
            }) => ({
              phaseTagId: Number(phaseTagId),
              phaseStageId: 0,
              startDate: phase.startDate,
              endDate: phase.endDate,
            })
          )
        )
        .filter((override) => {
          const originalPhase = classPhaseProps.find(
            (p) => p.phaseTagId === override.phaseTagId && p.limitStateToOne
          );
          return (
            originalPhase &&
            (override.startDate !== originalPhase.phaseStartDates[groupId] ||
              override.endDate !== originalPhase.phaseEndDates[groupId])
          );
        });
      onPhaseChange?.(overrides);

      return next;
    });
  };

  const currentTime = new Date();

  return (
    <CoursePhaseContainer>
      <Collapsible
        header={<div>{messages.phases}</div>}
        defaultOpen={defaultOpen}
      >
        {Object.entries(phaseTime || ({} as IPhaseTimeSpan)).map(
          ([stageKey, phasesMap]) => {
            const stageId = Number(stageKey);

            return (
              <div
                key={stageKey}
                style={{
                  border: `1px solid ${black}`,
                  borderRadius: '0.5rem',
                  padding: '0.25rem 0.5rem',
                }}
                className="my-2"
              >
                <h5 className="font-bold mt-2">
                  {messages.stage} {convertDigitToProperFormat(stageId + 1)}
                </h5>
                {Object.values(phasesMap || {})
                  .sort(
                    (a: any, b: any) =>
                      tags[a.phaseId].order! - tags[b.phaseId].order!
                  )
                  .map((phase: any) => (
                    <PhaseRow key={phase.phaseId}>
                      <h6 className="font-bold">
                        {tags?.[phase.phaseId]?.text}
                      </h6>

                      <PhaseRowContainer>
                        <MultiDatePicker
                          id={`${stageId}-${phase.phaseId}-start`}
                          setDate={(value: any) =>
                            handleChangeCoursePhaseTime(
                              stageId,
                              phase.phaseId,
                              {
                                startDate: Number(value),
                                endDate: phase.endDate
                                  ? phase.endDate +
                                    Number(value) -
                                    Number(phase.startDate)
                                  : Number(value) + ONE_DAY,
                              }
                            )
                          }
                          value={Number(phase.startDate) ?? null}
                          hasTime={true}
                          inputStyle={
                            phase.startDate && phase.startDate < currentTime
                              ? { color: red }
                              : phase.isStartOverride
                                ? { color: blue }
                                : {}
                          }
                        />
                        <div className="px-1">{messages.until}</div>
                        <MultiDatePicker
                          id={`${stageId}-${phase.phaseId}-end`}
                          setDate={(value) => {
                            const numericValue = Number(value);
                            const shouldUpdateStart =
                              numericValue < phase.startDate ||
                              !phase.startDate;

                            handleChangeCoursePhaseTime(
                              stageId,
                              phase.phaseId,
                              {
                                endDate: numericValue,
                                ...(shouldUpdateStart
                                  ? { startDate: numericValue - ONE_DAY }
                                  : {}),
                              }
                            );
                          }}
                          value={Number(phase.endDate) ?? null}
                          hasTime={true}
                          inputStyle={
                            phase.startDate && phase.endDate < currentTime
                              ? { color: red }
                              : phase.isEndOverride
                                ? { color: blue }
                                : {}
                          }
                        />
                      </PhaseRowContainer>
                    </PhaseRow>
                  ))}
              </div>
            );
          }
        )}

        {Object.keys(withoutPhase)?.length ? (
          <div
            className="mt-1"
            style={{
              border: `1px solid ${black}`,
              borderRadius: '0.5rem',
              padding: '0.25rem 0.5rem',
            }}
          >
            {Object.entries(withoutPhase).flatMap(([tagId, phases]) =>
              phases.map(
                (
                  noPhase: {
                    startDate: number;
                    endDate: number;
                    phaseId: number;
                  },
                  idx: number
                ) => (
                  <PhaseRow key={`without-${tagId}-${idx}`}>
                    <h6 className="font-bold">
                      {tags?.[noPhase.phaseId]?.text}
                    </h6>

                    <PhaseRowContainer>
                      <MultiDatePicker
                        id={`0-no-phase-${idx}-start`}
                        setDate={(value) =>
                          handleChangeWithoutPhaseTime(Number(tagId), idx, {
                            startDate: Number(value),
                            endDate: noPhase.endDate
                              ? noPhase.endDate +
                                Number(value) -
                                Number(noPhase.startDate)
                              : Number(value) + ONE_DAY,
                          })
                        }
                        value={Number(noPhase.startDate) ?? null}
                        hasTime={true}
                      />
                      <div className="px-1">{messages.until}</div>
                      <MultiDatePicker
                        id={'0-no-phase-end'}
                        setDate={(value: any) => {
                          const numericValue = Number(value);
                          const shouldUpdateStart =
                            numericValue < noPhase.startDate ||
                            !noPhase.startDate;

                          handleChangeWithoutPhaseTime(Number(tagId), idx, {
                            endDate: numericValue,
                            ...(shouldUpdateStart
                              ? { startDate: numericValue - ONE_DAY }
                              : {}),
                          });
                        }}
                        value={Number(noPhase.endDate) ?? null}
                        hasTime={true}
                      />
                    </PhaseRowContainer>
                  </PhaseRow>
                )
              )
            )}
          </div>
        ) : null}
      </Collapsible>
    </CoursePhaseContainer>
  );
};

export default CoursePhaseList;
